import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { DatePickerField, TextArea } from '../containers/Form';
import { closeModal } from '../actions/modal';
import { notificationActions } from '../components/Notification/redux/actions';
import Strings from '../Strings';
import Wizard from '../containers/Modal/Wizard';
import { extractErrorMessage, extractExceptionMessage, INVOICE_STATUSES, parseDateYMD } from '../pages/SuperUser/DeviceInvoices/helpers';

export const CHANGE_INVOICES_STATUS_MODAL_NAME = 'change-invoices-status-modal';

function ChangeInvoicesStatusModal(props) {
  const { data, closeModalWithNextAction, showNotification } = props;
  const { rows, targetStatus, action, actionType, successMessage, text, title } = data || {};
  const affectedRowsCount = rows?.length || 0;
  const affectedRowsWarning = affectedRowsCount === 1
    ? Strings.deviceInvoices.batchActionSingleWarning
    : Strings.formatString(Strings.deviceInvoices.batchActionWarning, affectedRowsCount);

  const [comment, setComment] = React.useState('');
  const [invoiceDate, setInvoiceDate] = React.useState(null);
  const [validationErrors, setValidationErrors] = React.useState([]);
  const [highlightInvalidFields, setHighlightMissingFields] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const requiresInvoiceDate = targetStatus === INVOICE_STATUSES.approved.key;
  const requiresComment = !requiresInvoiceDate;

  React.useEffect(() => {
    const errors = [];
    if (requiresInvoiceDate && !invoiceDate) {
      errors.push({ property: 'invoice_date', propertyName: Strings.deviceInvoices.invoiceDate, missing: true, errors: [] });
    }
    if (requiresComment && !comment?.trim()) {
      errors.push({ property: 'comment', propertyName: Strings.reason, missing: true, errors: [] });
    }
    setValidationErrors(errors);
  }, [comment, invoiceDate, requiresComment, requiresInvoiceDate]);

  const onSubmit = () => {
    setIsSaving(true);

    const formattedInvoiceDate = parseDateYMD(invoiceDate);
    const payload = {
      invoice_ids: rows.map(row => row.invoice_uuid),
      ...(targetStatus !== undefined ? { target_status: targetStatus } : {}),
      ...(requiresComment ? { comment } : {}),
      ...(requiresInvoiceDate ? { invoice_date: formattedInvoiceDate } : {}),
    };

    action(payload)
      .then(resp => {
        if (resp.type === `${actionType}/result`) {
          showNotification(`${successMessage}: ${resp.response?.updated || rows.length}`, 5000, false);
          closeModalWithNextAction();
          return;
        }
        showNotification(extractErrorMessage(resp), 5000, true);
        setIsSaving(false);
      })
      .catch(error => {
        showNotification(extractExceptionMessage(error), 5000, true);
        setIsSaving(false);
      });
  };

  const pages = [
    {
      id: 'change-invoices-status-page',
      title,
      content: (
        <>
          <div>{text}</div>
          {requiresInvoiceDate && (
            <DatePickerField
              name="invoice_date"
              id="invoice_date"
              label={Strings.deviceInvoices.invoiceDate}
              value={invoiceDate || ''}
              onChange={date => setInvoiceDate(date)}
              isRequired
              highlightInvalid={highlightInvalidFields}
              errorsForTooltip={validationErrors.find(v => v.property === 'invoice_date')?.errors || []}
            />
          )}
          {requiresComment && (
            <TextArea
              name="comment"
              id="comment"
              label={Strings.reason}
              value={comment}
              onChange={e => setComment(e.target.value)}
              highlightInvalid={highlightInvalidFields}
              isRequired
              errorsForTooltip={validationErrors.find(v => v.property === 'comment')?.errors || []}
            />
          )}
          {affectedRowsCount > 0 && (
            <div className="horizontal-flex unequal-children left">
              <div className="signal-alert relative info" />
              <div>
                {affectedRowsWarning}
              </div>
            </div>
          )}
        </>
      ),
      emptyFieldsCount: validationErrors.filter(e => e.missing).length,
      canGoNext: !isSaving && validationErrors.length === 0,
    },
  ];
  
  return (
    <Wizard
      name={CHANGE_INVOICES_STATUS_MODAL_NAME}
      pages={pages}
      onSubmit={onSubmit}
      onNextButtonHover={e => setHighlightMissingFields(e)}
    />
  );
}

ChangeInvoicesStatusModal.propTypes = {
  data: PropTypes.shape({
    rows: PropTypes.arrayOf(PropTypes.shape({
      invoice_uuid: PropTypes.string.isRequired,
    })).isRequired,
    targetStatus: PropTypes.string,
    action: PropTypes.func.isRequired,
    actionType: PropTypes.string.isRequired,
    successMessage: PropTypes.string.isRequired,
    text: PropTypes.string,
    title: PropTypes.string,
    onSuccess: PropTypes.func,
  }),
  closeModalWithNextAction: PropTypes.func.isRequired,
  showNotification: PropTypes.func.isRequired,
};


const mapDispatchToProps = (dispatch, ownProps) => ({
  showNotification: (message, timeout, error) => dispatch(notificationActions.show(message, timeout, error)),
  closeModalWithNextAction: () => {
    dispatch(closeModal(CHANGE_INVOICES_STATUS_MODAL_NAME));
    if (ownProps.data?.onSuccess) ownProps.data.onSuccess();
  },
});

export default connect(null, mapDispatchToProps)(ChangeInvoicesStatusModal);
